import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { AdmissionCareDayService } from 'app/um/services/admission-care-day-service';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { IPopup } from 'app/shared/elements/popup-base';
import { apiCall } from 'app/shared/lib/api/api';
import { DateValue, DateTimeValue } from 'app/shared/lib/date-time';
import { deepClone } from 'app/shared/lib/utilities';
import { modelIdMatcher } from 'app/shared/lib/matcher';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { validate } from 'app/shared/elements/ess-rule';
import { INestedLocalSorting, localSort } from 'app/shared/lib/sorting';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { IExtIqcSettings } from 'app/ext/iqc/ext-iqc';
import * as UM from 'app/um/models/essette-api';

interface ICareDayGroup extends UM.AdmissionCareDay {
    _index: number;
    _days: UM.AdmissionCareDay[];
    _nextDueDate?: string;
}

@autoinject()
export class CareDaysGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reloadCareDays' })
    public auth: UM.Auth | null = null;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = true;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public minimal: boolean = false;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public includeInactive: boolean = false;

    private days: UM.AdmissionCareDay[] = [];
    protected saveCareDays: UM.AdmissionCareDay[] = [];
    protected viewCareDay: UM.AdmissionCareDay | null = null;
    protected careDayGroups: ICareDayGroup[] = [];
    protected careDayGroupsClone: ICareDayGroup[] = [];

    protected pageSize = 1000;
    private sorting: Sorting = new Sorting();
    private nestedLocalSorting: INestedLocalSorting = {};
    protected resultCount: number | null = 0;

    protected $isValid: boolean = true;
    protected validateForm = validate;

    protected hasStatusHistory: boolean = false;

    protected iqcSettings: IExtIqcSettings = {
        allowOpen: true,
        showOpenReviewSummaryPdf: true
    }

    ////****************** Element References ***************************************////
    protected $modalSave!: IPopup;
    protected $modalStatusHistory!: IPopup;
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly admissionCareDayService: AdmissionCareDayService,
        private readonly context: AuthPageContext,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        if(!this.auth) {
            this.auth = this.context.auth;
        }

        this.checkPermissions();

        this.reloadCareDays();

    }


    ////****************** Events ***************************************************////
    protected edit(days: UM.AdmissionCareDay[]): void {
        if(!days || !this.$modalSave) {
            return;
        }

        const careDays = deepClone(days);
        careDays.forEach(day => {
            if(!day.status?.isActive) {
                day.status = null;
            }
        });

        this.saveCareDays = careDays;
        this.$modalSave.open();
    }

    protected editStatusHistory(day: UM.AdmissionCareDay): void {
        if(!day || !this.$modalStatusHistory) {
            return;
        }

        this.viewCareDay = deepClone(day);
        this.$modalStatusHistory.open();
    }

    protected add(): void {
        if(!this.$modalSave) {
            return;
        }

        this.saveCareDays = [];
        this.$modalSave.open();
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasStatusHistory = this.accountContext.hasPermission('UM.AdmissionCareDayStatusHistory.Query');
    }

    protected authChanged(newValue: UM.Auth, oldValue: UM.Auth): void {
        if(newValue.admission && newValue.admission.careDays &&
            (!oldValue.admission ||
                newValue.admission.careDays !== oldValue.admission.careDays)) {
            this.reloadCareDays();
        }
    }

    //Handles the group grid's sort event
    protected applySortGroups(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;

        // Resets nestedLocalSort
        this.nestedLocalSorting = {};
        this.careDayGroups.map((group, index) => {
            group._days = deepClone(this.careDayGroupsClone[group._index]._days || []);
        });

        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        if(this.sorting.entries.length === 0) {
            this.careDayGroups = deepClone(this.careDayGroupsClone);
        } else {
            localSort(this.careDayGroups, sorting);
        }

    }

    //Handles the care day grid's sort event
    protected applySortDays(event: SortChangeEvent, days: UM.AdmissionCareDay[], index: number, parent: ICareDayGroup): void {
        const { property, direction } = event.detail;
        const { nestedLocalSorting } = this;
        nestedLocalSorting[index] = nestedLocalSorting[index] || new Sorting();

        if(direction === null) {
            nestedLocalSorting[index].remove(property);
        } else {
            nestedLocalSorting[index].add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        if(this.nestedLocalSorting[index].entries.length > 0) {
            localSort(days, nestedLocalSorting[index]);
        } else {
            const copyIndex = this.careDayGroups[index]._index;
            this.careDayGroups[index]._days = deepClone(this.careDayGroupsClone[copyIndex]._days || []);
        }
    }

    private async reloadCareDays(): Promise<boolean> {
        if(this.auth && this.auth.id) {
            //we have paging here, so the main api call sets the response-count
            const days = await this.loadCareDays(this.auth.id, true) as UM.AdmissionCareDay[];

            const sorting: Sorting = new Sorting();
            sorting.add({ reference: "careDate", direction: SortDirection.Ascending });
            localSort(days, sorting);

            if(!this.auth.admission) {
                this.auth.admission = {}
            }

            this.days = days //for the grid

            if(!this.readonly) {
                //alldays has to be reloaded without resetting that count
                const alldays = this.includeInactive ? days : await this.loadCareDays(this.auth.id, false);
                this.auth.admission.careDays = alldays; //for the save-care-day modal
            }

            (<any>window).careDayGroups = this.careDayGroups = await this.buildCareDayGroups();
            this.careDayGroupsClone = deepClone(this.careDayGroups); // Clone for resetting sort
            localSort(this.careDayGroups, this.sorting);

            return true;
        }
        return false;
    }

    private isNextDayInGroup(previousDay: UM.AdmissionCareDay | undefined, currentDay: UM.AdmissionCareDay): boolean {
        // Inactive days are ungrouped
        if(!previousDay || !currentDay || !previousDay.careDate || !currentDay.careDate || !currentDay.isActive || !previousDay.isActive) return false;

        let pDate: DateValue = new DateValue(previousDay.careDate);
        let cDate: DateValue = new DateValue(currentDay.careDate);

        // Non-sequential days are ungrouped
        if(pDate.add(1, 'days').value !== cDate.value) return false;

        // If the current day status does not match the prior day’s status, do not group.
        if(!modelIdMatcher(
            previousDay.status,
            currentDay.status)
        ) return false;

        // If the current day status reasons do not match the prior day, do not group.
        if(previousDay.status?.reasons?.length != currentDay.status?.reasons?.length) return false;

        for(let index = 0; index < previousDay.status!.reasons!.length; index++) {
            if(!modelIdMatcher(
                previousDay.status?.reasons?.[index],
                currentDay.status?.reasons?.[index])
            ) return false;
        }

        // If the current day’s Actual Level Of Care does not match the prior day, do not group.
        if(!modelIdMatcher(
            previousDay.levelOfCare?.actual,
            currentDay.levelOfCare?.actual)
        ) return false;

        // If the current day’s Approved Level of Care does not match the prior day, do not group.
        if(!modelIdMatcher(
            previousDay.levelOfCare?.approved,
            currentDay.levelOfCare?.approved)
        ) return false;

        // If the current day’s Guideline does not match the prior day, do not group.
        if(previousDay.guideline !== currentDay.guideline) return false;

        return true;
    }

    protected calculateNextDueDate(careDays: UM.AdmissionCareDay[]): string | undefined {
        let firstDateTime: string | undefined = undefined;

        careDays.forEach((day) => {
            if(!day.dueDateTime) return;

            if(day.status && day.status.flag && !day.status.flag.isInitialStatus) return;

            if(!firstDateTime || day.dueDateTime < firstDateTime) {
                firstDateTime = day.dueDateTime;
            }
        });

        return firstDateTime;
    }

    protected getMaxDate(careDays: UM.AdmissionCareDay[]): string | undefined {
        let last: DateValue = DateValue.MIN_VALUE;

        for(let careDay of careDays) {
            if(!careDay.careDate) continue;

            let date: DateValue = new DateValue(careDay.careDate);

            if(date.isAfter(last)) {
                last = date;
            }
        }

        return last.toString();
    }

    protected getMinDate(careDays: UM.AdmissionCareDay[]): string | undefined {
        let first: DateValue = DateValue.MAX_VALUE;

        for(let careDay of careDays) {
            if(!careDay.careDate) continue;

            let date: DateValue = new DateValue(careDay.careDate);

            if(date.isBefore(first)) {
                first = date;
            }
        }

        return first.toString();
    }

    protected isPastDue(careDays: UM.AdmissionCareDay[], dueDate?: string | null | undefined): boolean {
        const firstCareDay = careDays[0] || careDays;

        if(!dueDate) {
            if(careDays instanceof Array) {
                dueDate = this.calculateNextDueDate(careDays);
            } else {
                dueDate = firstCareDay.dueDateTime;
            }
        }

        if(!dueDate || !firstCareDay) return false;

        return DateTimeValue.now().isAfter(new DateTimeValue(dueDate))
            && (!!firstCareDay.status && !!firstCareDay.status.flag && !!firstCareDay.status.flag.isInitialStatus);
    }

    protected async buildCareDayGroups(): Promise<ICareDayGroup[]> {
        let previousDay: UM.AdmissionCareDay | undefined;
        let currentDaysGroup: UM.AdmissionCareDay[];
        let groups: ICareDayGroup[] = [];
        let index = 0;

        for(let day of this.days) {
            let sameGroup: boolean = this.isNextDayInGroup(previousDay, day);

            if(!sameGroup) {
                currentDaysGroup = [];
                currentDaysGroup.push(day);
                // A pseudo ID based on index for the group since it will be used for individual group sorting and resetting the sort
                groups.push({ _index: index++, _days: currentDaysGroup, ...day } as ICareDayGroup);
            }
            else {
                currentDaysGroup!.push(day);
            }
            previousDay = day;
        }

        for(let group of groups) {
            group._nextDueDate = this.calculateNextDueDate(group._days);
        }

        return groups;
    }

    protected async formSuccess({ detail }: FormSuccessCustomEvent): Promise<void> {
        if(!!this.$modalSave) {
            this.$modalSave.close();
            this.reloadCareDays();
        }
    }

    protected applyTableDisplay(careDays: UM.AdmissionCareDay[]): UM.AdmissionCareDay[] {
        return careDays.map((careDay) => {
            return {
                ...careDay,
                $essTableDisplay: careDay.careDate ? new Date(careDay.careDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) : null
            } as UM.AdmissionCareDay;
        });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCareDays(authId: string, setCount: boolean): Promise<UM.AdmissionCareDay[]> {
        if(!authId) { return []; }

        const options: Partial<IQueryLoadOptions> = {
            all: true,
            sorting: this.sorting,
            includeInactive: setCount ? this.includeInactive : true
        };

        const includes: string[] = [
            'levelOfCare.actual',
            'levelOfCare.approved',
            'status',
            'status.reasons'
        ];

        const fields: string[] = [
            'careDate',
            'dueDateTime',
            'glos',
            'guideline',
            'levelOfCare.actual.description',
            'levelOfCare.approved.description',
            'metadata.created.timestamp',
            'product',
            'status.description',
            'status.flag.isInitialStatus',
            'status.reasons.description'
        ];

        if(!this.minimal) {
            includes.push(
                'authMedicalReview',
                'authMedicalReview.content',
                'auth.externalSystem'
            );

            fields.push(
                'authMedicalReview.*',
                'auth.externalSystem.code'
            );
        }

        if(!this.readonly) {
            includes.push(
                'status.authStatus'
            );

            fields.push(
                'status.authStatus.flag.isApproval'
            );
        }

        try {
            this.state.beginLoad();
            const response = await this.admissionCareDayService.loadCareDays(authId, options, includes, fields);
            var r = this.applyTableDisplay(response.results);
            if(setCount) this.resultCount = response.count || 0;
            return r;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateCareDay(careDay: UM.AdmissionCareDay, isActive: boolean) {
        if(!isActive) {
            const result = await confirmation.showDeactivate('um:auth.careDays.$');
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        }

        try {
            this.state.beginSave();
            const response = await this.admissionCareDayService.activateCareDay(careDay.id!, isActive);

            this.toaster.standard.activate('um:auth.careDays.$', isActive, response.isSuccessful);

            if(response.isSuccessful) {
                this.reloadCareDays();
                this.context.triggerCorrespondenceRules();
            }

        } catch(error) {
            this.toaster.standard.activate('um:auth.careDays.$', isActive, false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }
}
